import React, { useState, useEffect, useLayoutEffect, useRef, forwardRef } from 'react';
import { Tab } from './components';
import { SkeletonLoader } from '../';
import { useQueryParams } from '../../hooks';
import { keyCode } from '../../helpers';

import './Tabs.css';

export const _Tabs = (
  {
    onTabChange,
    showResultCount,
    resultMessage,
    showNoResultsMessage = true,
    disableTabsWithoutResults,
    defaultSelectedTab,
    className,
    loading,
    orientation = 'horizontal',
    tabLabel,
    children,
  },
  ref
) => {
  const { queryParams, setHistory, resetParams } = useQueryParams();

  const [activeTab, setActiveTab] = useState(defaultSelectedTab?.tab);

  const refTabs = useRef([]);

  children = Array.isArray(children) ? children.filter(x => x) : [children]; // If only one child, children will not be an array

  useEffect(() => {
    refTabs.current = new Array(children.length);
  }, []);

  useLayoutEffect(() => {
    children &&
      children.length > 0 &&
      setActiveTab(defaultSelectedTab?.tab || children[0].props.tabid);
  }, [defaultSelectedTab]);

  const onClickTabItem = tab => {
    setActiveTab(tab);

    // Call external function if provided
    if (onTabChange) {
      onTabChange(tab);
    } else {
      resetParams({ tab: tab });
    }
  };

  const focusNextListItem = direction => {
    const activeElement = document.activeElement;

    const activeElementIndex = refTabs.current.findIndex(el => el === activeElement);
    const activeTabIndex =
      activeElementIndex < 0
        ? children.findIndex(el => el.props.tabid === activeTab)
        : activeElementIndex;

    if (activeTabIndex >= 0) {
      if (direction === keyCode.RIGHT_ARROW && activeTabIndex + 1 < refTabs.current.length) {
        refTabs.current[activeTabIndex + 1].focus();
      } else if (direction === keyCode.LEFT_ARROW && activeTabIndex > 0) {
        refTabs.current[activeTabIndex - 1].focus();
      }
    } else {
      refTabs.current[0].focus();
    }
  };

  const handleKeyDown = (e, val) => {
    switch (e.keyCode) {
      case keyCode.LEFT_ARROW:
        focusNextListItem(keyCode.LEFT_ARROW);
        break;

      case keyCode.RIGHT_ARROW:
        focusNextListItem(keyCode.RIGHT_ARROW);
        break;

      default:
        break;
    }
  };

  return (
    <div
      className={
        'CustomTabs' +
        (className ? ' ' + className : '') +
        (showResultCount ? ' show-result-count' : '') +
        (orientation ? ' tab__' + orientation : '')
      }
      name="Tabs"
    >
      <div className="tab-header">
        <div className="tab-list" role="tablist" onKeyDown={handleKeyDown}>
          {tabLabel && <div className="tab-list-label">{tabLabel}</div>}
          {children.map((child, i) => {
            if (!child || !child.props) return null;

            // Only use divs as tab items
            if (child.type !== 'div') {
              return child;
            }

            const { label, tabid, resultCount, disabled, tooltip } = child.props;

            return (
              <Tab
                ref={el => (refTabs.current[i] = el)}
                activeTab={activeTab}
                key={tabid}
                label={label}
                tabid={tabid}
                resultCount={resultCount}
                onClick={onClickTabItem}
                disableTab={(disableTabsWithoutResults && resultCount === 0) || disabled}
                tooltip={tooltip}
              />
            );
          })}
        </div>
      </div>

      <div className="tab-body">
        <div className="tab-content-wrapper" ref={ref} role="tabpanel">
          {children.map((child, index) => {
            if (!child || !child.props) return undefined;

            const { label, tabid, resultCount, disabled } = child.props;

            if (tabid !== activeTab) return undefined;

            if (
              !loading &&
              ((disableTabsWithoutResults &&
                tabid !== activeTab &&
                resultCount === 0 &&
                showNoResultsMessage) ||
                disabled ||
                (!child.props.children && showNoResultsMessage))
            ) {
              return (
                <div className="tab-content-empty-text" key={index}>
                  No assets
                </div>
              );
            }

            return (
              <div className="tab-content" key={index}>
                {showResultCount && (
                  <div className="result-count small-text">
                    {loading ? (
                      <div>&nbsp;</div>
                    ) : (
                      <div>
                        {resultCount > 0 &&
                          resultCount +
                            ' ' +
                            (tabid !== 'all' ? label : 'assets') +
                            ' found' +
                            (resultMessage ? ` for ${resultMessage}` : '')}
                      </div>
                    )}
                  </div>
                )}
                {child.props.children}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export const Tabs = forwardRef(_Tabs);
